import type { Locale } from "@/lib/i18n/registry";
import type { LocaleView, PageSchema } from "../blocks/types";
import { casarEmPortugalDocumentos } from "./casar-em-portugal-documentos";
import { moneyCasamento } from "./money-casamento";
import { organizacaoDeCasamentos } from "./organizacao-de-casamentos";

/**
 * Схемы страниц по локали (BTQ-47, Этап 1).
 *
 * Ключ — тот же, что в реестре маршрутов. Значение говорит, чем страница
 * является на каждом языке:
 *
 * { kind: "component" } — страницу отдаёт её собственный React-компонент.
 * Так живёт вся английская сторона; так же может жить и португальская там,
 * где композиция действительно совпадает (контакты — именно такой случай:
 * это форма, а не текст под запрос).
 *
 * { kind: "blocks", … } — страница описана списком блоков и рендерится общим
 * рендерером. Это путь для португальских страниц, форма которых отличается
 * от английской: каталог с числом и гео, пошаговый гайд, подборка идей.
 *
 * Страница, которой здесь нет, считается component на обоих языках.
 *
 * ── Карта v2, шаг A2 (26.08.2026) ──
 *
 * Блочная форма понижена из режима ПО УМОЛЧАНИЮ в именованное исключение:
 * португальская страница — это английская страница на другом языке, тот же
 * компонент и та же композиция. Блоки остаются только там, где иную форму
 * требует спецификация из btq.pt/pt-core/pt-specs-v2/ — таких три:
 * venues, service-wedding-planning, pt-money-casamento (см. src/content/specs.ts).
 *
 * Партия 2 (услуги, 26.08): из таблицы убраны `services`,
 * `service-creative-direction`, `service-wedding-coordination` и
 * `service-private-concierge` — они переехали на свои компоненты.
 * `service-wedding-planning` ОСТАЁТСЯ блочной: спецификация pt-specs-v2/02
 * требует пошагового гайда, и правило 7 гейта это проверяет.
 * Португальский текст убранных страниц не потерян — он переехал в контент-слой
 * компонентов, а старые блочные схемы лежат нетронутыми.
 *
 * Партия 4 (26–27.08): убраны `about`, `portfolio`, `faq` — переехали на свои
 * компоненты. `legal` ОСТАЁТСЯ блочной СОЗНАТЕЛЬНО: её португальский текст
 * прошёл аудит по первоисточникам и механическую сверку legalcheck.py, а
 * попытка разложить право по слотам английской страницы дала десять
 * недостоверных утверждений (см. шапку legal-requirements.ts).
 *
 * Решение владельца 27.08: `venues` убран — каталог quintas перестаёт быть
 * блочным исключением, /pt/quintas-para-casamentos/ рендерится ТЕМ ЖЕ
 * компонентом, что английский /venues/ («hub venues вообще не клонирован»).
 * Файл quintas-para-casamentos.ts НЕ удаляется: его 2 443 слова португальского —
 * источник для перевода Фазы B. ⚠️ Вместе с блочной формой уходит формат,
 * который SERP-замер BTQ-155 выбрал как выигрышный (каталог с числом и гео) —
 * владелец предупреждён в отчёте BTQ-47.
 *
 * `home` из этой таблицы УБРАН: главная переехала на свой компонент
 * (src/pages/Index.tsx + контент-слой src/content/pages/home.ts). Португальский
 * текст из `home-eb.ts` при этом НЕ ПОТЕРЯН — файл лежит нетронутым и ждёт
 * шага A4, который переносит его в контент-слой пословно. A2 не пишет ни
 * строки перевода, поэтому переносить его сейчас нельзя.
 */
export const PAGE_SCHEMAS: Record<string, PageSchema> = {
  contact: {
    en: { kind: "component" },
    pt: { kind: "component" },
  },

  // Этап 2, волна A — eventboutique.pt. Все страницы описаны блоками, потому
  // что форма каждой отличается от английской пары: португальская выдача
  // вознаграждает другие форматы. Публикация — только после нативного
  // редактора; в прод попадает мержем, а не переводом в mirrored.
  "service-wedding-planning": organizacaoDeCasamentos,
  "pt-money-casamento": moneyCasamento,
  legal: casarEmPortugalDocumentos,
};

const COMPONENT_VIEW: LocaleView = { kind: "component" };

export function viewFor(key: string, locale: Locale): LocaleView {
  return PAGE_SCHEMAS[key]?.[locale] ?? COMPONENT_VIEW;
}
